import capitalize from "lodash/capitalize";
import { type RubyEventSummaryReport } from "../services/backend/RubyData";
import { currencyFormatter } from "./currency";
import { type KeywordMetricsReportDetails, type MetricKey, type MetricsReport } from "./metrics";
import { numberFormatter, percentFormatter, safeDivide, sumReducer } from "./number";
import { languageString } from "./text";
import { addGranularity, getTemporalRange, type ReportGranularity, type Temporal } from "./time";

export type EventsList = string[];

type EventSummaryReportTotals = RubyEventSummaryReport["totals"];
export interface EventReport extends EventSummaryReportTotals {
    //revenue: number;
    //unique: number;
    events: number;
    revenuePerEvent: number;
    revenuePerUniqueEvent: number;
    // May be computed on the fly in some cases
    costPerEvent?: number;
    costPerUniqueEvent?: number;
    returnOnSpend?: number;
}

export type EventKey = keyof Omit<EventReport, "totals">;
export interface TemporalEventReport extends Temporal, EventReport {
    // date: number;
    // granularity: ReportGranularity;
}

export interface EventKeywordReport extends EventReport {
    keyword: string;
}

export interface EventKeywordTemporalReport extends TemporalEventReport {
    keyword: string;
}

export interface EventSummaryReport extends EventReport {
    name: string;
}

export interface EventKeywordSummaryReport extends EventKeywordReport, KeywordMetricsReportDetails {
    costPerEvent: number;
}

export function eventNameToLabel(eventName: string) {
    if (!eventName) {
        return "";
    }

    eventName = eventName.replace(/^(Adjust_|AppsFlyer_af_|AppsFlyer_|af_)(.+?)/gi, "$2");
    eventName = eventName.replace(/_/gi, " ").trim();

    return capitalize(eventName);
}

export function createEventsSpendReport(
    rubyReport: RubyEventSummaryReport | EventReport,
    metrics: MetricsReport
): Required<EventReport> {
    if (!rubyReport) {
        return null;
    }
    if (!isEventReport(rubyReport)) {
        rubyReport = createEventsReport(rubyReport);
    }
    return {
        ...rubyReport,
        costPerEvent: safeDivide(metrics?.spend, rubyReport.events),
        costPerUniqueEvent: safeDivide(metrics?.spend, rubyReport.unique),
        returnOnSpend: safeDivide(rubyReport.revenue, metrics?.spend),
    };
}

function isEventReport(report: object): report is EventReport {
    return !!report && "revenue" in report && "unique" in report && "events" in report;
}

export function createEventsReport(rubyReport: RubyEventSummaryReport): EventReport {
    return calculateAdditionalEvents({
        ...rubyReport.totals,
        events: rubyReport.count,
    });
}

export function calculateAdditionalEvents(
    report: Omit<EventReport, "revenuePerEvent" | "revenuePerUniqueEvent">
): EventReport {
    return {
        ...report,
        revenuePerEvent: safeDivide(report.revenue, report.events),
        revenuePerUniqueEvent: safeDivide(report.revenue, report.unique),
    };
}

export function aggregateEvents(events: EventReport[]): EventReport {
    const totalEvents: EventReport = {
        events: 0,
        revenue: 0,
        revenuePerEvent: 0,
        revenuePerUniqueEvent: 0,
        unique: 0,
    };
    for (const key in totalEvents) {
        totalEvents[key] = events?.reduce<number>(sumReducer(key as keyof EventReport), 0) ?? 0;
    }
    return calculateAdditionalEvents(totalEvents);
}

export interface CampaignRoasReport {
    totalRevenue: number;
    spend: number;
    returnOnSpend: number;
    purchaseEventCount: number;
}

/**
 * Per-keyword revenue totals aggregated across every registered attribution event.
 * Used to drive the per-keyword ROAS metric on the keyword list and detail pages
 * (ticket: "Enable ROAS as a metric on keyword views").
 *
 * `keyword` is the keyword **text** to match against `KeywordMetricsReportDetails.text`.
 */
export interface KeywordRevenueReport {
    keyword: string;
    revenue: number;
    events: number;
    unique: number;
}

/**
 * Pseudo-metric key for the per-keyword ROAS toggle on the keyword list and
 * detail pages (parallels `bidAmount`'s pseudo-key handling). Kept in this
 * shared module rather than the page components so that both keyword pages
 * can import it without creating circular dependencies between them.
 */
export const KEYWORD_ROAS_KEY = "keywordRoas";

export type KeywordColumnKey = MetricKey | "bidAmount" | typeof KEYWORD_ROAS_KEY;

/**
 * Per-event count buckets keyed by the event name, then the keyword text
 * (`eventName -> keywordText -> count`). Used to render event-count columns on
 * the keywords list when the user pins an event via the Customize Metrics &
 * Events picker (ticket: "Show event performance on the keywords tab").
 */
export type EventCountByEventByKeyword = Map<string, Map<string, number>>;

/**
 * Build a fast `(eventName, keywordText) -> count` lookup from the per-event
 * keyword breakdown returned by `selectEventKeywordsByName`. Memoize the
 * result at the call site — it allocates two layers of Map.
 */
export function buildEventCountLookup(
    byEvent: ReadonlyMap<string, EventKeywordReport[]> | null | undefined
): EventCountByEventByKeyword {
    const result: EventCountByEventByKeyword = new Map();
    if (!byEvent) return result;
    for (const [eventName, rows] of byEvent) {
        const inner = new Map<string, number>();
        for (const row of rows) {
            inner.set(row.keyword, Number(row.events) || 0);
        }
        result.set(eventName, inner);
    }
    return result;
}

/**
 * ROAS for a single keyword: `revenue / spend`.
 *
 * Returns null when the keyword's spend is missing/zero so callers can suppress
 * the value (avoids ∞%/NaN% displays). Zero-revenue keywords return 0 — they
 * still represent a real ROAS data point ("we spent on this keyword and got nothing").
 */
export function calculateKeywordRoas(
    revenue: number | null | undefined,
    spend: number | null | undefined
): number | null {
    const safeSpend = Number(spend);
    if (!Number.isFinite(safeSpend) || safeSpend <= 0) {
        return null;
    }
    const safeRevenue = Number(revenue) || 0;
    return safeDivide(safeRevenue, safeSpend);
}

/**
 * Aggregated ROAS across every event that produced revenue in the date range
 * (the campaign's "purchase events"), as opposed to ROAS split by each event name.
 *
 * "Purchase event" is defined as any event with revenue > 0. There is no purchase /
 * install / subscription taxonomy on AttributionEvent today, so this rule is config-free
 * and naturally excludes events like installs or screen views that never carry revenue.
 *
 * Returns null when there is nothing meaningful to aggregate (no purchase events, or
 * spend is missing/zero), so callers can hide the tile rather than render 0% or ∞%.
 */
export function aggregateCampaignRoas(
    eventSummaries: EventSummaryReport[] | null | undefined,
    spend: number | null | undefined
): CampaignRoasReport | null {
    if (!eventSummaries) {
        return null;
    }
    const purchaseEvents = eventSummaries.filter((e) => Number(e.revenue) > 0);
    if (purchaseEvents.length === 0) {
        return null;
    }
    const safeSpend = Number(spend);
    if (!Number.isFinite(safeSpend) || safeSpend <= 0) {
        return null;
    }
    const totalRevenue = purchaseEvents.reduce((sum, e) => sum + Number(e.revenue || 0), 0);
    return {
        totalRevenue,
        spend: safeSpend,
        returnOnSpend: safeDivide(totalRevenue, safeSpend),
        purchaseEventCount: purchaseEvents.length,
    };
}

export function aggregateEventsAtGranularity(
    reports: TemporalEventReport[],
    granularity: ReportGranularity,
    from?: number,
    to?: number
): TemporalEventReport[] {
    //TODO this currently assumes we start with HOURLY input data

    if (!from || !to) {
        [from, to] = getTemporalRange(reports);
    }

    const reportGroups = generateEmptyTemporalReport(from, to, granularity);

    reports.forEach((report) => {
        const groupIdx = reportGroups.findIndex(
            (group) => group.date <= report.date && addGranularity(group.date, granularity) > report.date
        );
        reportGroups[groupIdx] = {
            ...reportGroups[groupIdx],
            ...aggregateEvents([reportGroups[groupIdx], report]),
        };
    });

    return reportGroups;
}

function generateEmptyTemporalReport(from: number, to: number, granularity: ReportGranularity): TemporalEventReport[] {
    const reportGroups: TemporalEventReport[] = [];
    let i = from;
    while (i <= to) {
        reportGroups.push({
            date: i,
            granularity,
            events: 0,
            unique: 0,
            revenue: 0,
            revenuePerEvent: 0,
            revenuePerUniqueEvent: 0,
        });
        i = addGranularity(i, granularity);
    }
    return reportGroups;
}

export function formatEvents(data: EventReport, currency: string): { [key in EventKey]: string } {
    return Object.entries(data).reduce((acc, [key, value]) => {
        acc[key] = eventFormatter(key as EventKey, currency)(value as number);
        return acc;
    }, {}) as { [key in EventKey]: string };
}

const eventLabelKeys: Record<string, string> = {
    revenue: "campaign.events.stat.revenue",
    events: "campaign.events.stat.events",
    unique: "campaign.events.stat.uniqueEvents",
    revenuePerEvent: "campaign.events.stat.revenuePerEvent",
    revenuePerUniqueEvent: "campaign.events.stat.revenuePerUniqueEvent",
    costPerEvent: "campaign.events.stat.costPerEvent",
    costPerUniqueEvent: "campaign.events.stat.costPerUniqueEvent",
    returnOnSpend: "campaign.events.stat.returnOnSpend",
};

export const eventLabels = new Proxy({} as Record<EventKey, string>, {
    get(_target, prop: string | symbol) {
        if (typeof prop === "string") {
            const dictKey = eventLabelKeys[prop];
            if (dictKey) {
                return languageString(dictKey);
            }
        }
        return undefined;
    },
}) as Record<EventKey, string>;

export const eventIcon = (eventId: string, statKey: keyof EventReport) => {
    switch (statKey) {
        case "revenue":
            return "Rocket";
        case "events":
            return "Target";
        case "unique":
            return "Impressions";
        case "revenuePerEvent":
            return "Chart";
        case "revenuePerUniqueEvent":
            return "ThumbsUp";
        case "costPerEvent":
            return "Search";
        case "costPerUniqueEvent":
            return "Tap";
        case "returnOnSpend":
            return "AB";
        default:
            return "Chart";
    }
};

export const eventFormatter = (statKey: keyof EventReport, currency: string) => {
    switch (statKey) {
        case "revenue":
        case "revenuePerEvent":
        case "revenuePerUniqueEvent":
        case "costPerEvent":
        case "costPerUniqueEvent":
            return currencyFormatter(currency);
        case "returnOnSpend":
            return percentFormatter(1);
        case "events":
        case "unique":
        default:
            return numberFormatter();
    }
};

export const eventDescending = (eventId: string, statKey: keyof EventReport) => {
    switch (statKey) {
        case "costPerEvent":
        case "costPerUniqueEvent":
            return true;
        case "revenue":
        case "revenuePerEvent":
        case "revenuePerUniqueEvent":
        case "events":
        case "unique":
        default:
            return false;
    }
};

export const eventNoColor = (eventId: string, statKey: keyof EventReport) => {
    switch (statKey) {
        case "revenue":
        case "revenuePerEvent":
        case "revenuePerUniqueEvent":
        case "costPerEvent":
        case "costPerUniqueEvent":
        case "events":
        case "unique":
        default:
            return false;
    }
};

export const eventIsPercent = (eventId: string, statKey: keyof EventReport) => {
    switch (statKey) {
        case "returnOnSpend":
            return true;
        case "revenue":
        case "revenuePerEvent":
        case "revenuePerUniqueEvent":
        case "costPerEvent":
        case "costPerUniqueEvent":
        case "events":
        case "unique":
        default:
            return false;
    }
};
